import { type ReactNode, useState } from 'react';
import { CloseIcon, LockIcon } from '../Icon.tsx';

/**
 * PIN girişi / PIN belirleme penceresi.
 *
 * ## İki kip
 *
 * - **belirle**: kullanıcı ilk kez bir PIN seçiyor (4–12 rakam).
 * - **doğrula**: kilitli bir kanal açılırken PIN soruluyor.
 *
 * PIN sunucuda **Argon2id özeti** olarak saklanır; buraya gönderilen rakamlar
 * yalnız TLS üzerinden gider ve sunucuda düz metin tutulmaz.
 */
export function PinDialog({
  mode,
  onSubmit,
  onCancel,
}: {
  mode: 'belirle' | 'dogrula';
  onSubmit: (pin: string) => Promise<void>;
  onCancel: () => void;
}): ReactNode {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    if (pin.length < 4) {
      setError('PIN en az 4 rakam olmalı.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(pin);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'PIN doğrulanamadı.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="live-tv__modal" role="dialog" aria-modal="true" aria-label="PIN">
      <div className="live-tv__modal-box">
        <div className="live-tv__modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-md)',
                background: 'rgba(255, 107, 107, 0.14)',
                color: 'var(--danger)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LockIcon size={20} />
            </div>
            <h2 className="live-tv__modal-title">
              {mode === 'belirle' ? 'PIN Belirle' : 'Çocuk Kilidi PIN Gerekli'}
            </h2>
          </div>
          <button
            type="button"
            className="live-tv__action-icon-btn"
            onClick={onCancel}
            title="Kapat"
          >
            <CloseIcon size={18} />
          </button>
        </div>

        <p className="live-tv__modal-text">
          {mode === 'belirle'
            ? 'Kilitli kanalları korumak için 4 haneli bir PIN seçin. PIN sunucuda Argon2id ile şifrelenerek saklanır.'
            : 'Bu kanal kilitlenmiştir. Canlı yayını izlemek için 4 haneli PIN kodunuzu girin.'}
        </p>

        <input
          className="input live-tv__pin-input"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={8}
          placeholder="••••"
          value={pin}
          onChange={(event) => setPin(event.target.value.replace(/\D/g, ''))}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void submit();
          }}
        />

        {error !== null && <p className="live-tv__error">{error}</p>}

        <div className="live-tv__modal-actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void submit()}
            disabled={busy}
          >
            {busy
              ? 'Kontrol ediliyor…'
              : mode === 'belirle'
                ? 'PIN Kaydet ve Kilitle'
                : 'Kilidi Aç'}
          </button>
        </div>
      </div>
    </div>
  );
}
